import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { RouterHistory } from '@tanstack/react-router';

import { AppShell } from './shell/AppShell';
import { LoginPage } from './features/auth/LoginPage';
import { sessionQueryOptions } from './features/auth/session';
import { isProfileTabId } from './features/people/profile-tab-ids';
import type { ProfileTabId } from './features/people/profile-tab-ids';
import { isAdminTabId } from './features/admin/admin-tab-ids';
import type { AdminTabId } from './features/admin/admin-tab-ids';

/**
 * Routing (§6 „Architektura informacji", §16.2 `@tanstack/react-router`, etap 5 zadanie 1).
 *
 * Routing OPARTY O KOD (bez generatora plików tras) — `@tanstack/router-plugin` nie jest
 * zależnością tego pakietu (`apps/web/package.json` ma tylko `@tanstack/react-router`), więc
 * drzewo tras jest budowane jawnie przez `createRoute`/`.addChildren`, zgodnie z oficjalnym,
 * w pełni wspieranym trybem „code-based routing" biblioteki.
 *
 * Zakres tras (etap 5 zadanie 1: `/login`, `/tree`, `/people`, `/people/:id`, `/sources`,
 * `/research`, `/history`, `/settings`; etap 6: `/trash`; etap 7 zadanie 7: `/sources/:id`,
 * `SourcesListRoute.tsx`/`SourceDetailRoute.tsx` zastępujące dawną zaślepkę `/sources`; etap 7
 * zadanie: `/review`, `/conflicts`; etap 8: `/transfers` — import i eksport,
 * `features/transfers/TransfersRoute.tsx`; etap 9: `/history` — historia, podgląd cofnięcia i
 * cofanie, `features/history/HistoryRoute.tsx`, zastępujące dawną zaślepkę; etap 11 zadanie 1:
 * `/admin` — powłoka panelu administratora z zakładkami (§23 wiersz 11), zakładka „Kopie
 * zapasowe" w pełni zbudowana na gotowym backendzie etapu 10, pozostałe cztery zakładki
 * (Konta/Audyt/Zadania/Importy) jako `PlaceholderScreen` na backend budowany równolegle przez
 * inny zespół) — węższy niż pełny katalog §6 (kolejne zadania domkną resztę `/admin`).
 */

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
});

/** §6: „Identyfikatory w adresach są niejawne; nie umieszczać nazwisk w URL" — `redirect` w
 * wyszukiwarce trasy logowania jest walidowany jako WEWNĘTRZNA ścieżka (patrz
 * `isSafeInternalPath`), więc nigdy nie staje się otwartym przekierowaniem na obcy host. */
function isSafeInternalPath(candidate: string): boolean {
  return candidate.startsWith('/') && !candidate.startsWith('//') && !candidate.includes('://');
}

export interface LoginSearch {
  redirect?: string;
}

function validateLoginSearch(search: Record<string, unknown>): LoginSearch {
  const redirectTo = search['redirect'];
  if (typeof redirectTo === 'string' && isSafeInternalPath(redirectTo)) {
    return { redirect: redirectTo };
  }
  return {};
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: validateLoginSearch,
  component: LoginPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/tree' });
  },
});

/**
 * Trasa-układ (bez własnej ścieżki) obejmująca wszystkie ekrany wymagające sesji (§17.1: „bez
 * sesji dostępny jest wyłącznie ekran logowania […] i niezbędne statyczne zasoby"). `beforeLoad`
 * czyta sesję PRZEZ `queryClient.ensureQueryData` — ten sam cache, z którego korzysta
 * `AppShell`/`LoginPage` (`sessionQueryOptions`), więc nawigacja i UI nigdy nie widzą dwóch
 * rozjechanych odpowiedzi na pytanie „kto jest zalogowany".
 */
export const protectedLayoutRoute = createRoute({
  id: 'protected-layout',
  getParentRoute: () => rootRoute,
  beforeLoad: async ({ context, location }) => {
    const user = await context.queryClient.ensureQueryData(sessionQueryOptions());
    if (user === null) {
      // `location.href` to ścieżka+search+hash BEZ origin (dokumentacja `ParsedLocation.href`)
      // — bezpieczny materiał na powrót po zalogowaniu (§5: „Powrót do dozwolonego adresu
      // wewnętrznego"), nigdy pełny URL z obcym hostem.
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    return { user };
  },
  component: ProtectedLayout,
});

function ProtectedLayout() {
  const { user } = protectedLayoutRoute.useRouteContext();
  return (
    <AppShell user={user}>
      <Outlet />
    </AppShell>
  );
}

/** §6/§8.1: identyfikatory w adresach są niejawne, ale osoba centralna drzewa MUSI być
 * adresowalna (odświeżenie strony, udostępniony link) — `rootId` w query string jest jedynym
 * sposobem, jak `/tree` dziś dostaje osobę centralną (wybór z listy/wyszukiwarki jest zadaniem
 * poza zamkniętym zakresem tego zadania, patrz `features/tree/TreeRoute.tsx`). Bez `rootId`
 * trasa pokazuje krótkie wyjaśnienie, nie fikcyjne dane (ADR 0010). */
export interface TreeSearch {
  rootId?: string;
}

function validateTreeSearch(search: Record<string, unknown>): TreeSearch {
  const rootId = search['rootId'];
  if (typeof rootId === 'string' && rootId.length > 0) {
    return { rootId };
  }
  return {};
}

/**
 * `lazyRouteComponent` (§18.3 „Drzewo jest osobnym chunkiem; worker ELK także”) — `TreeRoute.tsx`
 * i wszystko, co importuje (`TreeCanvas.tsx`, `@xyflow/react`, `elkjs`, `layout.worker.ts`),
 * wchodzi do bundla WYŁĄCZNIE przy nawigacji na `/tree`, nigdy do pakietu powłoki
 * (`main.tsx`/`router.tsx`/`AppShell.tsx`) — potwierdzone pomiarem po `npm run build`
 * w raporcie zadania.
 */
const treeRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/tree',
  validateSearch: validateTreeSearch,
  component: lazyRouteComponent(() => import('./features/tree/TreeRoute')),
});

/**
 * `/people` i `/people/$id` (§6, §9, §7.3 wireframe PROFIL, etap 6 część interfejsu) — zastąpiły
 * dawne zaślepki (`PlaceholderScreen`). Oba ładowane LENIWIE (`lazyRouteComponent`, §18.3), ten
 * sam wzorzec co `treeRoute` wyżej — lista i profil osoby (React Query per-zakładka, `useQueries`
 * w `PersonEvidenceTab.tsx`) nie muszą wchodzić do chunku powłoki.
 */
/** Fraza wyszukiwania przekazana z wyszukiwarki w nagłówku (`shell/GlobalSearch.tsx`, „Pokaż
 * wszystkie wyniki”) — jawna i opcjonalna w query string, tym samym wzorcem co
 * `validateTreeSearch`/`validatePersonSearch` wyżej. `PeopleListRoute.tsx` czyta ją WYŁĄCZNIE
 * jako wartość początkową swojego własnego pola wyszukiwania (dalej działa identycznie bez
 * parametru) — nigdy jako jedyne źródło prawdy o wpisanej frazie. Pusty/brakujący/nie-tekstowy
 * parametr wraca jako `{}`, NIGDY jako błąd nawigacji (ten sam duch co pozostałe `validateSearch`
 * tego pliku). */
export interface PeopleSearch {
  q?: string;
}

function validatePeopleSearch(search: Record<string, unknown>): PeopleSearch {
  const q = search['q'];
  return typeof q === 'string' && q.trim().length > 0 ? { q } : {};
}

const peopleRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/people',
  validateSearch: validatePeopleSearch,
  component: lazyRouteComponent(() => import('./features/people/PeopleListRoute')),
});

/** Stan aktywnej zakładki profilu w query string (§7.3 „Podsumowanie | Relacje | Oś czasu |
 * Dowody”) — jawny i zwalidowany wobec zamkniętego katalogu `PROFILE_TAB_IDS`
 * (`features/people/profile-tab-ids.ts`), tym samym wzorcem co `validateTreeSearch` wyżej.
 * Nieznana/brakująca wartość wraca jako `{}` (profil pokazuje domyślnie „Podsumowanie”,
 * `PersonProfileRoute.tsx`), NIGDY jako błąd nawigacji — zakładka jest wygodą UI, nie identyfikatorem. */
export interface PersonSearch {
  tab?: ProfileTabId;
}

function validatePersonSearch(search: Record<string, unknown>): PersonSearch {
  const tab = search['tab'];
  return isProfileTabId(tab) ? { tab } : {};
}

const personRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/people/$id',
  validateSearch: validatePersonSearch,
  component: lazyRouteComponent(() => import('./features/people/PersonProfileRoute')),
});

/**
 * Kosz (§14.2, A06, ADR 0012). Ładowany leniwie, jak pozostałe trasy poza drzewem (§18.3):
 * to widok używany rzadko, a jego kod nie ma obciążać powłoki.
 */
const trashRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/trash',
  component: lazyRouteComponent(() => import('./features/trash/TrashRoute'), 'TrashRoute'),
});

/**
 * `/sources` i `/sources/$id` (§6, §10.9, §9 wiersz „Źródła i cytowania", etap 7 §23 zadanie 7) —
 * zastąpiły dawną zaślepkę (`PlaceholderScreen`). Oba ładowane LENIWIE (`lazyRouteComponent`,
 * §18.3), ten sam wzorzec co `peopleRoute`/`personRoute` wyżej.
 */
const sourcesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/sources',
  component: lazyRouteComponent(() => import('./features/sources/SourcesListRoute')),
});

const sourceDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/sources/$id',
  component: lazyRouteComponent(() => import('./features/sources/SourceDetailRoute')),
});

/**
 * `/research` (§6, §10.8, §9 wiersze „Notatki badawcze"/„Hipotezy / sprzeczności", ADR 0016,
 * etap 7 warsztat badawczy) — zastępuje dawną zaślepkę (`PlaceholderScreen`). Ładowana LENIWIE
 * (`lazyRouteComponent`, §18.3), ten sam wzorzec co `sourcesRoute`/`peopleRoute` wyżej.
 */
const researchRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/research',
  component: lazyRouteComponent(
    () => import('./features/research/ResearchWorkshopRoute'),
    'ResearchWorkshopRoute',
  ),
});

/**
 * Kolejka recenzji i konflikty (§6 wymienia obie trasy, ADR 0017, kryteria A15/A13). Ładowane
 * leniwie, jak pozostałe trasy poza drzewem (§18.3).
 *
 * Pozycje nawigacji do obu tras są bramkowane uprawnieniem `edit` — uzasadnienie doboru wobec
 * macierzy §4 znajduje się przy `NAV_ITEMS` w `shell/nav-items.ts` i nie jest tu powtarzane.
 */
const reviewRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/review',
  component: lazyRouteComponent(() => import('./features/review/ReviewQueueRoute')),
});

const conflictsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/conflicts',
  component: lazyRouteComponent(() => import('./features/review/ConflictsListRoute')),
});

/**
 * `/transfers` (§6, §7.3 wireframe IMPORT, §13.1–§13.3, ADR 0019, ADR 0020, etap 8 — domknięcie
 * interfejsu importu/eksportu). Ładowana LENIWIE (`lazyRouteComponent`, §18.3), ten sam wzorzec
 * co `sourcesRoute`/`researchRoute`/`reviewRoute` wyżej — ekran używany rzadko (administracja/
 * redakcja), jego kod (w tym replika SHA-256 odcisku planu, `features/transfers/plan-hash.ts`)
 * nie ma obciążać powłoki.
 */
const transfersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/transfers',
  component: lazyRouteComponent(() => import('./features/transfers/TransfersRoute')),
});

/**
 * `/history` (§6, §9 wiersz „Historia", §14.2, ADR 0024, etap 9 — domknięcie historii i
 * cofania) — zastępuje dawną zaślepkę (`PlaceholderScreen`). Ładowana LENIWIE
 * (`lazyRouteComponent`, §18.3), ten sam wzorzec co `transfersRoute`/`reviewRoute` wyżej.
 */
const historyRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/history',
  component: lazyRouteComponent(() => import('./features/history/HistoryRoute')),
});

/**
 * `/settings` (audyt UI, znalezisko 5 „Zmiana hasła jest całkowicie niedostępna z poziomu
 * aplikacji") — zastępuje dawną zaślepkę (`PlaceholderScreen`). Ładowana LENIWIE
 * (`lazyRouteComponent`, §18.3), ten sam wzorzec co `historyRoute`/`transfersRoute` wyżej: dane
 * konta i formularz zmiany hasła (`features/settings/AccountSettingsRoute.tsx`) nie mają
 * obciążać powłoki, a ten ekran jest odwiedzany rzadko.
 */
const settingsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('./features/settings/AccountSettingsRoute')),
});

/** Stan aktywnej zakładki panelu administratora w query string — DOKŁADNIE ten sam wzorzec co
 * `validatePersonSearch` wyżej (`PersonSearch`/`isProfileTabId`): nieznana/brakująca wartość
 * wraca jako `{}` (zakładka domyślna „Kopie zapasowe", `AdminRoute.tsx`), NIGDY jako błąd
 * nawigacji — zakładka jest wygodą UI, nie identyfikatorem (§6). */
export interface AdminSearch {
  tab?: AdminTabId;
}

function validateAdminSearch(search: Record<string, unknown>): AdminSearch {
  const tab = search['tab'];
  return isAdminTabId(tab) ? { tab } : {};
}

/**
 * `/admin` (§23 wiersz 11 „Panel administratora", etap 11 zadanie 1) — powłoka z zakładkami,
 * zastępuje MIEJSCE po dawnym „bez `/admin/users`" z komentarza zakresu wyżej. Ładowana LENIWIE
 * (`lazyRouteComponent`, §18.3), ten sam wzorzec co `historyRoute`/`transfersRoute` wyżej —
 * ekran administracyjny używany rzadko, jego kod (w tym warstwa zapytań backupów) nie ma
 * obciążać powłoki. Widoczność pozycji nawigacji „Administracja" jest bramkowana uprawnieniem
 * `backup` (`shell/nav-items.ts`) — WYŁĄCZNIE ozdoba, sama trasa i `BackupsTab.tsx` sprawdzają
 * uprawnienie samodzielnie (§4 „uprawnienia egzekwowane w API, a nie tylko przez ukrywanie
 * przycisków").
 */
const adminRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/admin',
  validateSearch: validateAdminSearch,
  component: lazyRouteComponent(() => import('./features/admin/AdminRoute')),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  protectedLayoutRoute.addChildren([
    treeRoute,
    peopleRoute,
    personRoute,
    trashRoute,
    sourcesRoute,
    sourceDetailRoute,
    researchRoute,
    reviewRoute,
    conflictsRoute,
    transfersRoute,
    historyRoute,
    adminRoute,
    settingsRoute,
  ]),
]);

function NotFoundScreen() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg p-4 text-ink">
      <p>Nie znaleziono strony.</p>
    </main>
  );
}

/**
 * §7.2 „Ładowanie: szkielety kart bez migotania" — `defaultPendingMs`/`defaultPendingMinMs`
 * sprawiają, że szkielet pojawia się TYLKO gdy przejście trwa dłużej niż 300 ms i, jeśli się
 * pojawi, zostaje widoczny co najmniej 300 ms — bez tego krótkie przejścia migoczą jednym
 * renderem szkieletu między dwoma stanami treści.
 */
function PendingSkeleton() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-screen items-center justify-center bg-bg text-ink-muted"
    >
      Wczytywanie…
    </div>
  );
}

/** `history` jest opcjonalne wyłącznie dla testów (`test-helpers/test-router.ts`) — w
 * aplikacji `main.tsx` nie podaje go, więc router używa domyślnej historii przeglądarki. */
export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context: { queryClient },
    ...(history === undefined ? {} : { history }),
    defaultPendingMs: 300,
    defaultPendingMinMs: 300,
    defaultPendingComponent: PendingSkeleton,
    defaultNotFoundComponent: NotFoundScreen,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
